export function attachDeferredMedia(root: HTMLElement) {
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileHero = window.matchMedia('(max-width: 760px)');
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  let disposed = false;
  const canPlay = () => !disposed && !document.hidden && !motion.matches;

  // Never reset an existing video buffer when a containing section intersects.
  const restoreImages = (scope: Element) => {
    (scope as HTMLElement).dataset.mediaReady = 'true';
    scope.querySelectorAll<HTMLImageElement>('img[data-defer-src]').forEach(img => {
      img.src = img.dataset.deferSrc!;
      delete img.dataset.deferSrc;
    });
  };
  const images = new IntersectionObserver(entries => entries.forEach(entry => {
    if (!entry.isIntersecting) return;
    restoreImages(entry.target);
    images.unobserve(entry.target);
  }), { rootMargin: '500px' });
  root.querySelectorAll('section[id]').forEach(section => {
    if (section.querySelector('img[data-defer-src]')) images.observe(section);
  });

  // Keep layout and searchable content intact while offscreen effects rest.
  const zones = Array.from(root.querySelectorAll<HTMLElement>('section[id]'));
  const nearby = new Set<Element>();
  const updateMotion = () => zones.forEach(zone => {
    zone.toggleAttribute('data-motion-paused', document.hidden || !nearby.has(zone));
  });
  zones.forEach(zone => zone.setAttribute('data-motion-paused', ''));
  const effects = new IntersectionObserver(entries => {
    entries.forEach(entry => { if (entry.isIntersecting) nearby.add(entry.target); else nearby.delete(entry.target); });
    updateMotion();
  }, { rootMargin: '150px' });
  zones.forEach(zone => effects.observe(zone));

  const hero = root.querySelector<HTMLVideoElement>('.background-video');
  const videos = Array.from(root.querySelectorAll<HTMLVideoElement>('.background-video, .s5i-planning-video'));
  const visible = new Set<Element>();
  let heroReady = false;
  let heroTimer: number | undefined;
  const restoreVideo = (video: HTMLVideoElement) => {
    let changed = false;
    video.querySelectorAll<HTMLSourceElement>('source[data-defer-src]').forEach(source => {
      if (source.getAttribute('src') !== source.dataset.deferSrc) {
        source.src = source.dataset.deferSrc!;
        changed = true;
      }
    });
    if (video.dataset.deferSrc && video.getAttribute('src') !== video.dataset.deferSrc) {
      video.src = video.dataset.deferSrc;
      changed = true;
    }
    if (changed) { video.preload = 'metadata'; video.load(); }
  };
  const syncVideos = () => videos.forEach(video => {
    if (video === hero) {
      // The mobile hero is solid black: never request its poster or video.
      if (mobileHero.matches) {
        video.pause();
        video.removeAttribute('poster');
        const loaded = video.hasAttribute('src') || !!video.querySelector('source[src]');
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        if (loaded) { video.preload = 'none'; video.load(); }
        return;
      }
      if (visible.has(video) && video.dataset.deferPoster && !video.hasAttribute('poster')) {
        video.poster = video.dataset.deferPoster;
      }
    }
    if (!visible.has(video) || !canPlay() || (video === hero && (!heroReady || connection?.saveData))) {
      video.pause();
      return;
    }
    restoreVideo(video);
    video.muted = true;
    if (video.paused) video.play().catch(() => {});
  });
  const playback = new IntersectionObserver(entries => {
    entries.forEach(entry => { if (entry.isIntersecting) visible.add(entry.target); else visible.delete(entry.target); });
    syncVideos();
  }, { threshold: 0.05 });
  videos.forEach(video => playback.observe(video));
  const onLoad = () => { heroTimer = window.setTimeout(() => { heroReady = true; syncVideos(); }, 1800); };
  if (document.readyState === 'complete') onLoad(); else window.addEventListener('load', onLoad, { once: true });
  const onVisibility = () => { updateMotion(); syncVideos(); };
  document.addEventListener('visibilitychange', onVisibility);
  motion.addEventListener('change', syncVideos);
  mobileHero.addEventListener('change', syncVideos);

  return () => {
    disposed = true;
    images.disconnect(); effects.disconnect(); playback.disconnect();
    window.removeEventListener('load', onLoad); window.clearTimeout(heroTimer);
    document.removeEventListener('visibilitychange', onVisibility);
    motion.removeEventListener('change', syncVideos);
    mobileHero.removeEventListener('change', syncVideos);
    root.querySelectorAll('video').forEach(video => {
      video.pause();
      video.removeAttribute('src');
      video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
      video.load();
    });
  };
}
